import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { SelectNativo } from '@/components/ui/select-nativo';
import type {
  CalendarioDeTienda,
  CambioDeCalendario,
  TipoDeLectura,
  UnidadDeFrecuencia,
} from '../types/prixia.types';
import { TIPO_DE_LECTURA, fechaYHora } from '../lib/prixia-textos';

interface Formulario {
  isActive: boolean;
  intervalValue: string;
  intervalUnit: UnidadDeFrecuencia;
  preferredHour: string;
}

/** Lo que propone el plan 291 (B3) para una tienda sin calendario. */
const POR_DEFECTO: Record<
  TipoDeLectura,
  { intervalValue: number; intervalUnit: UnidadDeFrecuencia }
> = {
  PRICE_CHECK: { intervalValue: 1, intervalUnit: 'DAYS' },
  FULL_RESCRAPE: { intervalValue: 1, intervalUnit: 'WEEKS' },
};

function desdeCalendario(tipo: TipoDeLectura, c: CalendarioDeTienda | undefined): Formulario {
  return {
    isActive: c?.isActive ?? false,
    intervalValue: String(c?.intervalValue ?? POR_DEFECTO[tipo].intervalValue),
    intervalUnit: c?.intervalUnit ?? POR_DEFECTO[tipo].intervalUnit,
    preferredHour: c?.preferredHour == null ? '' : String(c.preferredHour),
  };
}

/**
 * Lo que identifica al calendario guardado: si no cambió, una recarga de la ficha (por
 * otra acción) no le borra a la persona lo que estaba editando.
 */
function huella(c: CalendarioDeTienda | undefined): string {
  if (!c) return '';
  return [
    c.changedAt,
    c.nextRunAt,
    c.isActive,
    c.intervalValue,
    c.intervalUnit,
    c.preferredHour,
  ].join('|');
}

interface Props {
  tipo: TipoDeLectura;
  calendario: CalendarioDeTienda | undefined;
  /** Con la tienda apagada no hay próxima lectura, diga lo que diga el calendario. */
  tiendaActiva?: boolean;
  onGuardar: (cambio: CambioDeCalendario) => Promise<void>;
}

/**
 * Una fila del calendario de una tienda de Prixia (plan 297): encendido, cada cuánto y
 * a qué hora. Rige para todas las empresas que tienen la tienda (C8).
 */
export function EditorDeCalendarioPrixia({
  tipo,
  calendario,
  tiendaActiva = true,
  onGuardar,
}: Props) {
  const [form, setForm] = useState<Formulario>(() => desdeCalendario(tipo, calendario));
  const [guardando, setGuardando] = useState(false);
  const ultimaHuella = useRef(`${tipo}:${huella(calendario)}`);

  useEffect(() => {
    const actual = `${tipo}:${huella(calendario)}`;
    if (actual === ultimaHuella.current) return;
    ultimaHuella.current = actual;
    setForm(desdeCalendario(tipo, calendario));
  }, [tipo, calendario]);

  const nombre = TIPO_DE_LECTURA[tipo];
  const valor = Number(form.intervalValue);
  const valido = Number.isInteger(valor) && valor >= 1 && valor <= 1000;

  const guardar = async () => {
    setGuardando(true);
    try {
      await onGuardar({
        isActive: form.isActive,
        intervalValue: valor,
        intervalUnit: form.intervalUnit,
        // «a las 06:00» es en punto: sin los minutos, quedarían los que tenía guardados.
        ...(form.preferredHour === ''
          ? { preferredHour: null }
          : { preferredHour: Number(form.preferredHour), preferredMin: 0 }),
      });
    } finally {
      setGuardando(false);
    }
  };

  const ultimoEstado = calendario?.lastRunStatus
    ? calendario.lastRunStatus === 'SUCCESS'
      ? ' (bien)'
      : ' (falló)'
    : '';

  return (
    <div className="grid gap-3 rounded-lg border p-3 lg:grid-cols-[14rem_1fr_auto] lg:items-center">
      <div>
        <p className="font-medium">{nombre}</p>
        <p className="text-xs text-muted-foreground">
          {`Próxima: ${
            !tiendaActiva
              ? '— (tienda apagada)'
              : calendario?.isActive
                ? fechaYHora(calendario.nextRunAt)
                : '—'
          } · Última: ${fechaYHora(calendario?.lastRunAt)}${ultimoEstado}`}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Switch
          checked={form.isActive}
          onCheckedChange={(v) => setForm({ ...form, isActive: v })}
          aria-label={`${nombre} encendido`}
        />
        <span className="w-20">{form.isActive ? 'Encendido' : 'Apagado'}</span>
        <span className="text-muted-foreground">cada</span>
        <Input
          type="number"
          min={1}
          max={1000}
          className="h-9 w-20"
          value={form.intervalValue}
          onChange={(e) => setForm({ ...form, intervalValue: e.target.value })}
          aria-label={`Cada cuánto: ${nombre.toLowerCase()}`}
        />
        <SelectNativo
          value={form.intervalUnit}
          onChange={(e) => setForm({ ...form, intervalUnit: e.target.value as UnidadDeFrecuencia })}
          aria-label={`Unidad: ${nombre.toLowerCase()}`}
        >
          <option value="HOURS">horas</option>
          <option value="DAYS">días</option>
          <option value="WEEKS">semanas</option>
        </SelectNativo>
        <span className="text-muted-foreground">a las</span>
        <SelectNativo
          value={form.preferredHour}
          onChange={(e) => setForm({ ...form, preferredHour: e.target.value })}
          aria-label={`Hora: ${nombre.toLowerCase()}`}
        >
          <option value="">cualquier hora</option>
          {Array.from({ length: 24 }, (_, h) => (
            <option key={h} value={String(h)}>
              {`${String(h).padStart(2, '0')}:00`}
            </option>
          ))}
        </SelectNativo>
      </div>
      <Button
        size="sm"
        onClick={() => void guardar()}
        disabled={!valido || guardando}
        aria-label={`Guardar ${nombre.toLowerCase()}`}
      >
        {guardando ? 'Guardando…' : 'Guardar'}
      </Button>
    </div>
  );
}

export default EditorDeCalendarioPrixia;
